import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Handles return from Emergent Google OAuth.
 * URL fragment looks like: /dashboard#session_id=<token>
 * Exchanges the session_id with our backend, which calls Emergent to fetch
 * user data + session_token, sets the httpOnly cookie, and returns the user.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const session_id = match ? decodeURIComponent(match[1]) : null;

    // Clean the URL immediately so the token never appears in analytics/referrers
    window.history.replaceState(null, "", window.location.pathname);

    if (!session_id) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        setUser(data);
        navigate("/dashboard", { replace: true, state: { user: data } });
      } catch (err) {
        const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
        setError(msg);
      }
    })();
    // Runs exactly once per mount (guarded by hasProcessed.current).
    // Imports + setters are stable; only navigation deps need tracking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setUser]);

  return (
    <div data-testid="auth-callback" className="relative min-h-screen bg-white text-neutral-900 flex items-center justify-center px-6 overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="aurora-orb aurora-orb-magenta orb-anim-a" style={{ width: 360, height: 360, top: -120, left: -120, opacity: 0.16 }} />
        <div className="aurora-orb aurora-orb-azure orb-anim-b" style={{ width: 360, height: 360, bottom: -120, right: -120, opacity: 0.14 }} />
      </div>
      {error ? (
        <div className="relative max-w-md text-center glass rounded-md p-8 sm:p-10">
          <p className="overline">Sign-in failed</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-3">
            Something went <span className="italic text-aurora">wrong.</span>
          </h1>
          <p className="text-neutral-500 mt-4">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="btn-aurora mt-6"
            data-testid="auth-callback-retry"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="relative text-center">
          <Loader2 size={28} className="animate-spin text-aurora-static mx-auto" />
          <p className="mt-5 overline">Signing you in&hellip;</p>
        </div>
      )}
    </div>
  );
}
